import { format } from "date-fns";

// Date when the new Nica schedule takes effect (UTC-6).
// From this date onward: 12pm slot becomes 11am, and 6pm only runs on weekends.
export const NICA_SCHEDULE_CHANGE_DATE = "2026-06-15";

export interface NicaSlot {
  /** Database column name in lottery_results */
  dbCol: "nica_11am" | "nica_3pm" | "nica_6pm" | "nica_9pm";
  /** Display label (e.g. "11:00 AM") */
  time: string;
  /** Short label used in the admin form (e.g. "11am") */
  short: string;
  /** Form value used by the admin form select */
  formValue: string;
}

const isWeekend = (date: Date): boolean => {
  const day = date.getDay(); // 0 = Sunday, 6 = Saturday
  return day === 0 || day === 6;
};

const isOnOrAfterChange = (dateStr: string): boolean =>
  dateStr >= NICA_SCHEDULE_CHANGE_DATE;

/**
 * Returns the Nica draw slots that apply for the given date.
 * The `date` argument is treated as a wall-clock date (already in
 * America/Costa_Rica when called from the app), so no further timezone
 * conversion is applied here — re-zoning would shift the day and break
 * the schedule cutover.
 *
 * - Before 2026-06-15: 12pm, 3pm, 6pm, 9pm (every day, with the old 12pm label)
 * - From 2026-06-15:  11am, 3pm, 9pm on weekdays; adds 6pm on weekends
 */
export const getNicaSlotsForDate = (date: Date): NicaSlot[] => {
  const dateStr = format(date, "yyyy-MM-dd");
  const newSchedule = isOnOrAfterChange(dateStr);
  const firstTime = newSchedule ? "11:00 AM" : "12:00 PM";
  const firstShort = newSchedule ? "11am" : "12md";
  const firstFormValue = newSchedule ? "nica_11am" : "nica_12pm";

  const slots: NicaSlot[] = [
    { dbCol: "nica_11am", time: firstTime, short: firstShort, formValue: firstFormValue },
    { dbCol: "nica_3pm", time: "3:00 PM", short: "3pm", formValue: "nica_3pm" },
  ];

  const include6pm = !newSchedule || isWeekend(date);
  if (include6pm) {
    slots.push({ dbCol: "nica_6pm", time: "6:00 PM", short: "6pm", formValue: "nica_6pm" });
  }

  slots.push({ dbCol: "nica_9pm", time: "9:00 PM", short: "9pm", formValue: "nica_9pm" });
  return slots;
};
